import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";

export interface StockInfo {
  id: UUID;
  name: string | null;
  weight: number;
  quantity: number;
  lot_number: string | null;
  location_name: string | null;
}

export interface ProductInfo {
  id: UUID;
  name: string;
  sku: string;
}

export interface UnitInfo {
  id: UUID;
  name: string;
  short_name: string;
}

export type TransferType =
  | "location"
  | "unit_change"
  | "transformation"
  | "split"
  | "merge";
export type TransferStatus =
  | "pending"
  | "in_transit"
  | "completed"
  | "cancelled";

export interface TransferResponse {
  id: UUID;
  company_id: UUID;
  transaction_id: UUID | null;
  user_id: UUID | null;
  transfer_type: TransferType;
  status: TransferStatus;
  source_stock_id: UUID | null;
  destination_stock_id: UUID | null;
  source_product_id: UUID;
  destination_product_id: UUID;
  source_unit_id: UUID;
  destination_unit_id: UUID;
  source_quantity: number;
  destination_quantity: number;
  source_weight: number;
  destination_weight: number;
  price: number | null;
  provisional: boolean;
  order_date: string | null;
  delivery_date: string | null;
  comment: string | null;
  hidden_comment: string | null;
  transfer_metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  // Relations (always included from list endpoint)
  source_stock: StockInfo | null;
  destination_stock: StockInfo | null;
  source_product: ProductInfo | null;
  destination_product: ProductInfo | null;
  source_unit: UnitInfo | null;
  destination_unit: UnitInfo | null;
}

export interface TransferWithRelationsResponse extends TransferResponse {
  source_stock: StockInfo | null;
  destination_stock: StockInfo | null;
  source_product: ProductInfo | null;
  destination_product: ProductInfo | null;
  source_unit: UnitInfo | null;
  destination_unit: UnitInfo | null;
}

export interface TransferCreate {
  transaction_id?: UUID | null;
  transfer_type?: TransferType;
  source_stock_id?: UUID | null;
  destination_stock_id?: UUID | null;
  source_product_id: UUID;
  destination_product_id: UUID;
  source_unit_id: UUID;
  destination_unit_id: UUID;
  source_quantity: number;
  destination_quantity: number;
  source_weight?: number;
  destination_weight?: number;
  price?: number | null;
  status?: TransferStatus;
  provisional?: boolean;
  order_date?: string | null;
  delivery_date?: string | null;
  comment?: string | null;
  hidden_comment?: string | null;
  transfer_metadata?: Record<string, any>;
}

export interface TransferUpdate {
  transfer_type?: TransferType;
  source_product_id?: UUID;
  destination_product_id?: UUID;
  source_stock_id?: UUID | null;
  destination_stock_id?: UUID | null;
  source_quantity?: number;
  destination_quantity?: number;
  source_weight?: number;
  destination_weight?: number;
  price?: number | null;
  provisional?: boolean;
  order_date?: string | null;
  delivery_date?: string | null;
  comment?: string | null;
  hidden_comment?: string | null;
  transfer_metadata?: Record<string, any>;
}

export interface TransferFilters {
  transaction_id?: UUID;
  status?: string;
  source_stock_id?: UUID;
  destination_stock_id?: UUID;
}

export default class APITransfers extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  list = async (filters?: TransferFilters): Promise<TransferResponse[]> => {
    const params = new URLSearchParams();
    if (filters?.transaction_id)
      params.append("transaction_id", filters.transaction_id);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.source_stock_id)
      params.append("source_stock_id", filters.source_stock_id);
    if (filters?.destination_stock_id)
      params.append("destination_stock_id", filters.destination_stock_id);

    const query = params.toString() ? `?${params.toString()}` : "";
    const res = await this._fetch({
      url: `/transfers/${query}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  listByStock = async (stockId: UUID): Promise<TransferResponse[]> => {
    const res = await this._fetch({
      url: `/transfers/by-stock/${stockId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  listByProduct = async (productId: UUID): Promise<TransferResponse[]> => {
    const res = await this._fetch({
      url: `/transfers/by-product/${productId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  get = async (id: UUID): Promise<TransferResponse> => {
    const res = await this._fetch({
      url: `/transfers/${id}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getWithRelations = async (
    id: UUID,
  ): Promise<TransferWithRelationsResponse> => {
    const res = await this._fetch({
      url: `/transfers/${id}/with-relations`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  create = async (data: TransferCreate): Promise<TransferResponse> => {
    const res = await this._fetch({
      url: `/transfers/`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  createBatch = async (data: TransferCreate[]): Promise<TransferResponse[]> => {
    const res = await this._fetch({
      url: `/transfers/batch`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  update = async (
    id: UUID,
    data: TransferUpdate,
  ): Promise<TransferResponse> => {
    const res = await this._fetch({
      url: `/transfers/${id}`,
      method: "PATCH",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  delete = async (id: UUID): Promise<void> => {
    await this._fetch({
      url: `/transfers/${id}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  start = async (id: UUID): Promise<TransferResponse> => {
    const res = await this._fetch({
      url: `/transfers/${id}/start`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  complete = async (id: UUID): Promise<TransferResponse> => {
    const res = await this._fetch({
      url: `/transfers/${id}/complete`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  cancel = async (id: UUID): Promise<TransferResponse> => {
    const res = await this._fetch({
      url: `/transfers/${id}/cancel`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };
}
